import { v4 as uuidv4 } from "uuid";
import { persistCustomFields } from 'api/third';
import {persistIndividual, defaultIndividualDetails, defaultIndividualAccountability, defaultIndividualParamSettings, getDefaultCategory, formatMetaTypes, createIndividual, getIndividualsMetadata} from "api/individuals";
import Third from 'stores/forms/third';

import {
    addressState,
    addressGetters,
    addressMutations,
    addressActions,
    persistAddresses,
} from "./addressHelpers";

const rules = {
    civility: [
        {
            required: true,
            message: window.i18n.__("Veuillez choisir une civilité"),
            trigger: "submit",
        },
    ],
    name: [
        {
            required: true,
            message: window.i18n.__("Veuillez renseigner un nom"),
            trigger: "blur",
        },
    ],
    email: [
        {
            pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w+)+$/,
            message: window.i18n.__(
                "Veuillez renseigner une adresse email valide"
            ),
            trigger: "blur",
        },
    ],
}

export default {
    namespaced: true,
    state() {
        return {
            ...Third.state(),
            rules,
            // DATA POPULATED BY SERVER FETCH
            types: [],
            civilities: [],
            accountingcodes: [],
            accountingPurchaseCodes: [],
            // FORM BINDING
            details: defaultIndividualDetails(),
            accountability: defaultIndividualAccountability(),
            rateCategories: [],
            // INDIVIDUAL : ADDRESS(ES)
            addressSection: addressState(),
            // SIDE PARAMS
            sideParams: {
                rateCategories: true,
                note: true,
                social: true,
                syncProviders: true,
                smarttags: false,
            },
            syncProvidersList: [],
            paramsSettings: defaultIndividualParamSettings(),
        };
    },
    getters: {
        // INDIVIDUAL - DETAILS
        ...Third.getters,
        // Given by server (metadada)
        getTypes: (state) => state.types,
        getType: (state) => state.details.type,
        getCivility: (state) => state.details.civility,
        getName: (state) => state.details.name,
        getFirstName: (state) => state.details.firstName,
        getEmail: (state) => state.details.email,
        getBirthDate: (state) => state.details.birthDate,
        getPhone: (state) => state.details.contact.phone,
        getMobile: (state) => state.details.contact.mobile,
        getFax: (state) => state.details.contact.fax,
        getTiersCodeList: (state) => state.accountingcodes,
        getTiersCode: (state) => state.accountability.tiers,
        getTiersCodeID: (state) => {
            const completeObject = state.accountingcodes.find(
                (code) => code.value === state.accountability.tiers
            );
            return completeObject?.id;
        },
        getPurchaseCodeList: (state) => state.accountingPurchaseCodes,
        getPurchaseCode: (state) => state.accountability.purchase,
        getPurchaseCodeID: (state) => {
            const completeObject = state.accountingPurchaseCodes.find(
                (code) => code.value === state.accountability.purchase
            );
            return completeObject?.id;
        },
        getAuxiliaryCode: (state) => state.accountability.auxiliary,
        // SIDE PARAMS
        getParamsConfig: (state) => state.sideParams,
        getRateCategoriesList: (state) => state.rateCategories,
        getRateCategories: (state) => state.paramsSettings.rateCategory,
        getNote: (state) => state.paramsSettings.note,
        getSyncProviders: (state) =>{
            let syncs = [];
            Object.entries(state.paramsSettings.syncProviders).forEach(([key, value]) =>{
                syncs[key] = value
            })
            return syncs;
        },
        getSyncProvidersList: (state) => {
            return state.syncProvidersList;
        },
        getSocial: (state) => state.paramsSettings.social,
        getFacebook: (state) => state.paramsSettings.social.facebook,
        getTwitter: (state) => state.paramsSettings.social.twitter,
        getLinkedin: (state) => state.paramsSettings.social.linkedin,
        // INDIVIDUAL - ADDRESS(ES)
        ...addressGetters,
        // RULES & MODELS FOR FORM VALIDATORS (on <El-form> component)
        getRules: (state) => state.rules,
        getModel: (state, getters) => {
            return {
                ...state.details,
                ...state.details.contact,
                ...state.paramsSettings.social,
                addresses: { ...state.addressSection.addresses },
                groupedCustomFields: getters.groupedCustomFields
            };
        },
    },
    mutations: {
        ...Third.mutations,
        initStoreWithMetadata(state, metaData) {
            state.types = formatMetaTypes(metaData.types);
            state.civilities = metaData.civilities;
            state.accountingcodes = metaData.accounting.accounting_codes.map(
                (code) => ({ value: code.label, id: code.value })
            );
            state.accountingPurchaseCodes = metaData.accounting.accounting_purchase_codes.map(
                (code) => ({ value: code.label, id: code.value })
            );
            state.rateCategories = metaData.rate_categories;
            state.paramsSettings.rateCategory = getDefaultCategory(
                state.rateCategories
            );
        },
        setIndividualType(state, type) {
            state.sideParams.rateCategories = type !== "supplier";
            state.details.type = type;
        },
        setIndividualCivility(state, civility) {
            state.details.civility = civility;
        },
        setIndividualName(state, name) {
            state.details.name = name;
        },
        setIndividualFirstName(state, firstName) {
            state.details.firstName = firstName;
        },
        setIndividualbirthDate(state, birthDate) {
            state.details.birthDate = birthDate;
        },
        setIndividualEmail(state, email) {
            state.details.email = email;
        },
        setPhone(state, phone) {
            state.details.contact.phone = phone;
        },
        setMobile(state, mobile) {
            state.details.contact.mobile = mobile;
        },
        setFax(state, fax) {
            state.details.contact.fax = fax;
        },
        setRateCategories(state, category) {
            state.paramsSettings.rateCategory = category;
        },
        setNote(state, note) {
            state.paramsSettings.note = note;
        },
        setSyncProviders: (state, payload) =>{
            state.paramsSettings.syncProviders.mailchimp.isSetup = _.isNil(payload.find(sync => sync.value ===  "mailchimp")) ? false : true;
            state.paramsSettings.syncProviders.mailjet.isSetup =  _.isNil(payload.find(sync => sync.value ===  "mailjet")) ? false : true;
            state.paramsSettings.syncProviders.simplemail.isSetup =  _.isNil(payload.find(sync => sync.value ===  "simplemail")) ? false : true;
        },
        setSyncProvidersList: (state, payload) => {
            state.syncProvidersList = payload.filter(sync => !!sync.is_setup);
        },
        setSocial(state, socialAddresses) {
            state.paramsSettings.social = socialAddresses;
        },
        setFacebook(state, facebookURL) {
            state.paramsSettings.social.facebook = facebookURL;
        },
        setTwitter(state, twitterURL) {
            state.paramsSettings.social.twitter = twitterURL;
        },
        setLinkedin(state, linkedinURL) {
            state.paramsSettings.social.linkedin = linkedinURL;
        },

        setAuxiliaryCode(state, code) {
            state.accountability.auxiliary = code;
        },
        setTiersCode(state, code) {
            state.accountability.tiers = code;
        },
        setPurchaseCode(state, code) {
            state.accountability.purchase = code;
        },
        resetForm(state) {
            state.details = defaultIndividualDetails();
            state.paramsSettings = defaultIndividualParamSettings();
            state.accountability = defaultIndividualAccountability();
        },

        // ADDRESSES
        ...addressMutations,
    },
    actions: {
        ...Third.actions,

        updateSyncProviders:({commit}, syncs) =>{
            commit("setSyncProviders", syncs)
        },
        async fetchMetadata({ commit, dispatch }) {
            try {
                const res = await getIndividualsMetadata();
                if (res.error) {
                    throw res.error;
                } else {
                    commit("initStoreWithMetadata", res.data);
                    dispatch("createNewAddress");
                    const syncs = res.data.sync_providers?.length ? res.data.sync_providers : []; 

                    commit("setSyncProvidersList", syncs)
                    commit("setSyncProviders", syncs)
                }
            } catch (err) {
                console.error(err)
                throw err;
            }
        },
        resetForm: ({ commit }) => {
            commit("resetForm");
            window.EventBus.$emit("form:third:create:setAddressAlgolia", "");
        },
        // ADDRESSES
        ...addressActions,
        // SUBMIT FORM
        async updateThird({ state, dispatch, getters }, action) {
            const uuid = uuidv4();
            
            const payloadToPost = [
                { uri: "/individuals", method: "POST",  "uuid": uuid },
                persistIndividual(state, getters),
            ];

            if(getters.customFields.length) {
                payloadToPost.push({ "uri": `/individuals/${uuid}/custom-fields`, "method": "PUT" }),
                payloadToPost.push(persistCustomFields(getters.customFields));
            }
                        // ADDRESS(ES)
            if (
                state.addressSection.addresses &&
                state.addressSection.addresses.length > 0
            ) {
                dispatch("formatAddressesBeforeSubmit");
                state.addressSection.addresses.forEach((address) => {
                    if (!address.active) {
                        payloadToPost.push({
                            uri: `/individuals/${uuid}/addresses`,
                            method: "POST",
                        });
                        payloadToPost.push(
                            persistAddresses(address)
                        );
                    }
                });
            }

            const formatToNDJSON = payloadToPost.map(JSON.stringify).join("\n");
            try {
                const { data } = await createIndividual(
                    formatToNDJSON
                );
                if (data.error) throw data.responses;
                Push.success(window.i18n.__("Particulier ajouté avec succès"));
                dispatch("resetForm");
                return {
                    id: data.responses[0].response.id,
                    type: data.responses[0].response.type,
                };
            } catch (err) {
                Push.error(window.i18n.__("Une erreur est survenue"));
            }
        },
    },
};
